import { useTranslation as useI18nTranslation } from "react-i18next";
import { useCallback } from "react";
import { useOptionalGsbNavigation } from "@gsb-core/app-kit";
import { AVAILABLE_LANGUAGES, getShortLanguageCode, safeChangeLanguage, } from "../config.js";
export const useFormattingLocale = () => {
    const navigation = useOptionalGsbNavigation();
    const segment = navigation?.location.pathname.split("/")[1];
    return Object.values(AVAILABLE_LANGUAGES).find((language) => language.code === segment ||
        getShortLanguageCode(language.code) === segment)?.code;
};
/**
 * Enhanced useTranslation hook with better typing and parameter support
 */
export const useTranslation = (ns, options) => {
    const { t: i18nT, i18n, ready: isReady } = useI18nTranslation(ns, options);
    const t = useCallback((key, options) => {
        try {
            let translationOptions;
            if (Array.isArray(options)) {
                translationOptions = options.reduce((acc, val, index) => {
                    acc[index.toString()] = val;
                    return acc;
                }, {});
            }
            else if (options !== null && typeof options === "object") {
                translationOptions = options;
            }
            const translated = i18nT(key, translationOptions);
            return typeof translated === "string" ? translated : key;
        }
        catch (error) {
            console.warn(`Translation error for key "${key}":`, error);
            return key;
        }
    }, [i18nT]);
    const changeLanguage = useCallback(async (lng) => {
        await safeChangeLanguage(lng);
    }, []);
    return {
        t,
        language: i18n.language,
        changeLanguage,
        isLoading: !isReady,
        isReady,
    };
};
// Namespace-specific hooks for better organization
export const useNamespaceTranslation = (namespace) => {
    return useTranslation(namespace);
};
export const useCommonTranslation = () => {
    return useTranslation("common");
};
export const useErrorTranslation = () => {
    return useTranslation("errors");
};
export const useEmotionTranslation = () => {
    return useTranslation("emotions");
};
export const useLocalizedDate = () => {
    const { language } = useTranslation();
    const formatDate = useCallback((date, options) => {
        const dateObj = typeof date === "string" ? new Date(date) : date;
        const locale = language; // Use language directly since it's already full locale
        const defaultOptions = {
            year: "numeric",
            month: "long",
            day: "numeric",
            ...options,
        };
        try {
            return new Intl.DateTimeFormat(locale, defaultOptions).format(dateObj);
        }
        catch {
            // Fallback to English if locale is not supported
            return new Intl.DateTimeFormat("en-US", defaultOptions).format(dateObj);
        }
    }, [language]);
    return { formatDate };
};
// Additional namespace hooks for specific sections
export const useLandingTranslation = () => {
    return useTranslation("landing");
};
export const useDashboardTranslation = () => {
    return useTranslation("dashboard");
};
export const useEmotionsTranslation = () => {
    return useTranslation("emotions");
};
export const useAuthTranslation = () => {
    return useTranslation("auth");
};
export const useSettingsTranslation = () => {
    return useTranslation("settings");
};
export const useReflectionTranslation = () => {
    return useTranslation("reflection");
};
export const useChatTranslation = () => {
    return useTranslation("chat");
};
export const useLegalTranslation = () => {
    return useTranslation("legal");
};
export const useErrorsTranslation = () => {
    return useTranslation("errors");
};
//# sourceMappingURL=useTranslation.js.map